import { ReactNode } from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';

/**
 * Width presets. The class strings are spelled out rather than built from a template,
 * because Tailwind scans source for literal class names — an interpolated
 * `sm:max-w-${size}` compiles to nothing and the modal silently renders full-bleed.
 */
const SIZES = {
  sm: 'sm:max-w-lg',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
} as const;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /**
   * Defaults to the width every existing caller was hardcoded to, so adding this
   * changed nothing for them. `lg`/`xl` are for content that is a table rather than a
   * form — a stage with nine seats has no chance in `sm`.
   */
  size?: keyof typeof SIZES;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'sm',
}: ModalProps) {
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div
          className={`relative inline-block bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full ${SIZES[size]}`}
        >
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Every caller used to supply its own Cancel button, so a dialog
                that is a VIEW rather than a form had no way out but Escape or
                a backdrop click — neither of which announces itself. */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mt-1 -mr-1 shrink-0 rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
